import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Button, FormHelperText, Stack, styled } from '@mui/material';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import TextStyle from '@tiptap/extension-text-style';
import Typography from '@tiptap/extension-typography';
import Placeholder from '@tiptap/extension-placeholder';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import History from '@tiptap/extension-history';
import CharacterCount from '@tiptap/extension-character-count';
import {
    FontSize,
    MenuButtonBold,
    MenuButtonItalic,
    MenuButtonUnderline,
    MenuControlsContainer,
    RichTextEditor as RichTextEditorComponent,
} from 'mui-tiptap';
import { COLORS } from '../../utils/Application_Constants';

const CHARACTER_LIMIT = 800;
const DEFAULT_FONT_SIZE = 14;

const StyledRichTextEditor = styled(RichTextEditorComponent)(({ theme, error }) => ({
    '&.MuiTiptap-FieldContainer-root, &.MuiTiptap-FieldContainer-focused': {
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadiusVariants.md,
        backgroundColor: COLORS.LT_MIDNIGHT5,

        '&:focus-visible': { outlineColor: COLORS.LT_MIDNIGHT },
    },

    '& .MuiTiptap-FieldContainer-notchedOutline': {
        padding: 0,
        borderRadius: theme.shape.borderRadiusVariants.md,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: error ? theme.palette.error.main : 'transparent',
        '& :hover, :focus': {
            borderColor: error ? theme.palette.error.main : COLORS.LT_MIDNIGHT,
        },
    },

    '&.MuiTiptap-FieldContainer-focused': {
        '& .MuiTiptap-FieldContainer-notchedOutline': {
            borderColor: error ? theme.palette.error.main : COLORS.LT_MIDNIGHT,
            '& :hover': { borderColor: error ? theme.palette.error.main : COLORS.LT_MIDNIGHT },
        },
    },

    '& .MuiTiptap-RichTextContent-root': {
        padding: 0,
        maxHeight: '150px',
        overflowY: 'auto',

        '& .ProseMirror': {
            minHeight: '98px',
            '& p': {
                fontSize: DEFAULT_FONT_SIZE,
                fontWeight: 400,
                lineHeight: '17px',
                color: COLORS.LT_MIDNIGHT,

                '&.is-empty::before': {
                    color: COLORS.LT_MIDNIGHT50,
                },
            },
        },
    },

    '.submit-button': {
        marginLeft: 'auto',
        minWidth: 54,
        borderRadius: ({ shape }) => `${shape.borderRadiusVariants.md}px`,
    },
}));

const RichTextEditor = forwardRef(
    ({ loading, placeholder = 'Add a comment', onUpdate, onSubmit, apiError }, ref) => {
        const rteRef = useRef(null);
        const [error, setError] = useState('');
        const [characterCount, setCharacterCount] = useState(0);
        const [activeControls, setActiveControls] = useState({
            bold: false,
            italic: false,
            underline: false,
        });

        // Initialize the editor
        useEffect(() => {
            const editor = rteRef.current?.editor;
            if (!editor) return;

            const updateFormattingStates = () => {
                setActiveControls({
                    bold: editor.isActive('bold'),
                    italic: editor.isActive('italic'),
                    underline: editor.isActive('underline'),
                });
            };

            editor.on('transaction', updateFormattingStates);
            return () => {
                editor.off('transaction', updateFormattingStates);
            };
        }, [rteRef]);

        const getHTML = () => rteRef.current?.editor?.getHTML();
        const getCharacterCount = () =>
            rteRef.current?.editor?.storage?.characterCount?.characters();
        const reset = () => {
            setCharacterCount(0);
            rteRef.current?.editor?.commands.setContent('');
        };
        const isEmpty =
            rteRef.current?.editor?.isEmpty || !rteRef.current?.editor?.getText().trim();

        // Expose reset method to parent component
        useImperativeHandle(ref, () => ({
            reset,
        }));

        const checkHasErrors = () => {
            const characterCountAux = getCharacterCount();

            if (characterCountAux > CHARACTER_LIMIT) {
                setError('Content exceeds character limit');
                return true;
            }

            setError('');
            return false;
        };

        const handleChange = () => {
            const content = getHTML();
            setCharacterCount(getCharacterCount());
            checkHasErrors(content);
            onUpdate?.(content);
        };

        const handleSubmit = () => {
            if (error) return;

            const content = getHTML();

            if (checkHasErrors(content)) return;

            onSubmit?.(content);
        };

        const hasCharacterLimitError = characterCount === 0 || characterCount > CHARACTER_LIMIT;

        return (
            <Box>
                <StyledRichTextEditor
                    ref={rteRef}
                    extensions={[
                        Document,
                        Paragraph,
                        Text,
                        Typography,
                        Bold,
                        Italic,
                        Underline,
                        TextStyle,
                        FontSize,
                        Placeholder.configure({ placeholder }),
                        History,
                        CharacterCount,
                    ]}
                    error={!!error}
                    onUpdate={handleChange}
                    RichTextFieldProps={{
                        variant: 'outlined',
                        footer: (
                            <Stack
                                sx={{
                                    mt: 1,
                                    pt: !error ? '12px' : undefined,
                                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                {error && (
                                    <FormHelperText error sx={{ fontSize: 14, mb: 1.5 }}>
                                        {error}
                                    </FormHelperText>
                                )}
                                {apiError != null && (
                                    <FormHelperText error sx={{ fontSize: 14, mb: 1.5 }}>
                                        {apiError}
                                    </FormHelperText>
                                )}
                                <Stack direction="row" alignItems="center" gap={2}>
                                    <MenuControlsContainer>
                                        <MenuButtonBold
                                            selected={activeControls.bold}
                                            data-testid="bold-button"
                                        />
                                        <MenuButtonItalic
                                            selected={activeControls.italic}
                                            data-testid="italic-button"
                                        />
                                        <MenuButtonUnderline
                                            selected={activeControls.underline}
                                            data-testid="underline-button"
                                        />
                                    </MenuControlsContainer>

                                    <Button
                                        disabled={hasCharacterLimitError || isEmpty || !!error}
                                        loading={loading}
                                        className="submit-button"
                                        variant="contained"
                                        size="small"
                                        onClick={handleSubmit}
                                    >
                                        Post
                                    </Button>
                                </Stack>
                            </Stack>
                        ),
                    }}
                />
                <FormHelperText
                    error={!!error && hasCharacterLimitError}
                    sx={{ mt: '15px', fontSize: 14, color: COLORS.LT_MIDNIGHT75 }}
                >
                    {characterCount}/{CHARACTER_LIMIT}
                </FormHelperText>
            </Box>
        );
    }
);

export default RichTextEditor;
